import { inject, Injectable, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { DataInstance, DataPackage, FieldValue, FileMeta, Schema, Variable } from '@apis/cas/generated';
import { db } from '@core/database/db';
import { LpBaseInstance, LpInstance } from '@typings/lp/generated';
import { filter, firstValueFrom } from 'rxjs';
import { AssetReaderService } from './asset-reader.service';
import { CAS_DATA_SERVICE } from './cas-data.interface';

export type DataReaderState = 'Initializing' | 'PrecacheReady' | 'DatabaseReady' | 'Error';

@Injectable({
  providedIn: 'root',
})
export class DataReaderService {
  state = signal<DataReaderState>('Initializing');
  private fieldTypeDictionaryCache: Record<
    string,
    Record<
      string,
      {
        type: keyof FieldTypeMapping;
        required: boolean;
      }
    >
  > = {};
  #casDataService = inject(CAS_DATA_SERVICE);
  #assetReaderService = inject(AssetReaderService);
  #showWarnings = false;
  #stateObservable = toObservable(this.state);
  #preDatabaseCache: LpInstance[] = [];
  #preDatabaseCacheVariables: Variable[] = [];

  constructor() {
    this.initializeData();
  }

  async getAllVariables(): Promise<Variable[]> {
    await this.waitForInitialization();

    return this.#preDatabaseCacheVariables;
  }

  async getVariable(id: string): Promise<Variable | undefined> {
    await this.waitForInitialization();

    return this.tryGetVariable(id);
  }

  public tryGetVariable(id: string): Variable | undefined {
    return this.#preDatabaseCacheVariables.find((variable) => variable.variableRef === id);
  }

  /**
   * Retrieves all instances of a specific data type from the data store.
   *
   * @template T The data type to filter by, must be a valid `LpInstance['dataType']`.
   * @param {T} type The data type to filter instances by.
   * @returns {readonly Extract<LpInstance, { dataType: T }>[]} An array of instances that match the specified data type.
   */
  async getInstancesByType<T extends LpInstance['dataType']>(type: T): Promise<Extract<LpInstance, { dataType: T }>[]> {
    await this.waitForInitialization();

    if (this.state() === 'PrecacheReady') {
      return this.#preDatabaseCache.filter((instance) => instance.dataType === type) as Extract<
        LpInstance,
        {
          dataType: T;
        }
      >[];
    }
    return (await db.instances.where('dataType').equals(type).toArray()) as Extract<LpInstance, { dataType: T }>[];
  }

  /**
   * Retrieves an instance from the data store or cache based on its unique identifier (UID).
   *
   * @param uid The unique identifier of the instance to retrieve.
   * @returns The instance with the matching UID, or undefined if no such instance exists.
   */
  async getInstanceByUid(uid: string): Promise<LpInstance | undefined> {
    await this.waitForInitialization();

    const instance = await db.instances.get(uid);
    return instance !== undefined ? instance : this.#preDatabaseCache.find((instance) => instance.uid === uid);
  }

  private async initializeData(): Promise<void> {
    try {
      const dataPackageVersion = await this.#casDataService.getDataPackageVersion();
      const storedDataPackageVersion = localStorage.getItem('dataPackageVersion');

      let dataPackage = (await db.dataPackages.get(dataPackageVersion))?.dataPackage;
      let schema: Schema;

      if (dataPackage) {
        console.log(`Using cached data package version: ${dataPackageVersion}`);
        schema = JSON.parse(localStorage.getItem('schema') || '{}') as Schema;
      } else {
        console.log(`Data package version changed from ${storedDataPackageVersion} to ${dataPackageVersion}. Fetching new data package.`);
        [dataPackage, schema] = await Promise.all([this.#casDataService.getDataPackage(), this.#casDataService.getSchema()]);

        //prevent accumulating datapackages for no reason
        db.dataPackages.clear();
        db.dataPackages.add({ version_id: dataPackageVersion, dataPackage: dataPackage });
        localStorage.setItem('dataPackageVersion', dataPackageVersion);
        localStorage.setItem('schema', JSON.stringify(schema));
      }

      // Always update the variables
      this.#preDatabaseCacheVariables = dataPackage.variables;

      /**
       * NOTE: Quick fix for expiration of URLs is to always parse data package.
       * TODO: Implement service worker so we can cache again and avoid lag on startup.e
       */
      //  if (storedDataPackageVersion !== dataPackageVersion || !allFileMetasCached) {
      //    if (storedDataPackageVersion === null) {
      //      console.log('First time parsing data package');
      //    } else if (storedDataPackageVersion !== dataPackageVersion) {
      //      console.log(`Data package version changed from ${storedDataPackageVersion} to ${dataPackageVersion}. Reparsing data package.`);
      //    } else {
      //      console.log('Reparsing data package without version change, to refresh the FileMetas.');
      //    }

      // const assetReaderFullyCachedBeforeParsing = this.#assetReaderService.state() === 'FullyCached';

      const parsedInstances = await this.parseDataPackage(dataPackage, schema);
      db.instances.clear();
      this.#preDatabaseCache = parsedInstances;
      this.state.set('PrecacheReady');
      const batchSize = 100;
      for (let i = 0; i < parsedInstances.length; i += batchSize) {
        const batch = parsedInstances.slice(i, i + batchSize);
        await db.instances.bulkAdd(batch);
      }

      console.log(`Database ready with ${parsedInstances.length} instances`);

      // Quick fix
      // if (assetReaderFullyCachedBeforeParsing) {
      //   localStorage.setItem('allFileMetasCached', 'true');
      // } else {
      //   localStorage.removeItem('allFileMetasCached');
      // }

      this.state.set('DatabaseReady');
    } catch (error) {
      console.error(error);
      this.state.set('Error');
    }
  }

  /**
   * Waits for the asset reader to finish initializing.
   *
   * @returns A promise that resolves when the asset reader's state is no longer 'Initializing'.
   */
  private async waitForInitialization() {
    await firstValueFrom(this.#stateObservable.pipe(filter((state) => state !== 'Initializing')));
  }

  private async parseDataPackage(dataPackage: DataPackage, schema: Schema): Promise<LpInstance[]> {
    const newObjects = dataPackage.instances.map((instance) => {
      return {
        uid: instance.uid,
        dataType: instance.dataType,
      };
    });

    const mappedObjects: LpBaseInstance[] = await Promise.all(
      dataPackage.instances.map((instance, index) => this.mapInstance(instance, newObjects, newObjects[index], schema)),
    );

    return this.handleMissingRequiredFields(mappedObjects, schema) as LpInstance[];
  }

  private async mapFieldValue(fv: FieldValueWithTypeInfo, mappedInstances: LpBaseInstance[], variables: Variable[]) {
    switch (fv.type) {
      case 'string':
        return fv.value;
      case 'int':
        return this.parseIntValue(fv);
      case 'float':
        return this.parseFloatValue(fv);
      case 'bool':
        return this.parseBoolValue(fv);
      case 'Color':
        return this.parseHexColorToRGBA(fv.value);
      case 'Vector2':
        return this.parseVector2(fv as FieldValueWithGenericType<'Vector2'>);
      case 'Vector3':
        return this.parseVector3(fv as FieldValueWithGenericType<'Vector3'>);
      case 'Icon':
        return fv.value;
      case 'ImageRef':
      case 'VideoRef':
      case 'AudioRef':
      case 'FileRef':
        return fv.value ? await this.#assetReaderService.getFileMeta(fv.value) : undefined;
      case 'VariableRef':
        return this.resolveVariableReference(fv, variables);
      case 'Select':
        return fv.value;
      case 'ObjectReference': // Struct<T>, StructRef<T>, Enum<T>, EnumRef<T>
        return this.resolveObjectReference(fv, mappedInstances);
      case 'ObjectReferenceList': //List<Struct<T>>, List<StructRef<T>>, List<Enum<T>>, List<EnumRef<T>>
        return this.resolveObjectReferenceList(fv, mappedInstances);
      default:
        console.warn(`Unknown field type: ${fv.type}`);
        return fv.value;
    }
  }

  private parseIntValue(fv: FieldValueWithTypeInfo): number {
    return parseInt(fv.value, 10);
  }

  private parseFloatValue(fv: FieldValueWithTypeInfo): number {
    return parseFloat(fv.value);
  }

  private parseBoolValue(fv: FieldValueWithTypeInfo): boolean {
    return fv.value === 'true';
  }

  private parseVector2(fv: FieldValueWithGenericType<'Vector2'>) {
    //example: "value": "0.5;0.5"
    const [x, y] = fv.value.split(';').map(parseFloat);
    return { x, y };
  }

  private parseVector3(fv: FieldValueWithGenericType<'Vector3'>) {
    //example: "value": "0.5;0.5;0.5"
    const [x, y, z] = fv.value.split(';').map(parseFloat);
    return { x, y, z };
  }

  private parseHexColorToRGBA(hexColor: string): {
    r: number;
    g: number;
    b: number;
    a: number;
  } {
    //assert that this is a valid hex color
    if (!/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{8})$/.test(hexColor)) {
      throw new Error('Invalid hex color');
    }

    // Remove the '#' from the hex color string
    hexColor = hexColor.replace('#', '');

    // Parse r, g, b values from the hex color string
    const r = parseInt(hexColor.substring(0, 2), 16);
    const g = parseInt(hexColor.substring(2, 4), 16);
    const b = parseInt(hexColor.substring(4, 6), 16);
    // If the hex color string has an alpha value, parse it, otherwise default to 1
    const a = hexColor.length === 8 ? parseInt(hexColor.substring(6, 8), 16) : 1;

    return { r, g, b, a }; // Assuming alpha is 1, if not provided in hex
  }

  private resolveObjectReference(fv: FieldValueWithTypeInfo, mappedInstances: LpBaseInstance[]): LpBaseInstance | undefined {
    if (!fv.value) {
      return undefined;
    }
    return this.findInstanceByUid(fv.value, mappedInstances);
  }

  private resolveObjectReferenceList(fv: FieldValueWithTypeInfo, mappedInstances: LpBaseInstance[]): LpBaseInstance[] {
    const uids = this.unwrapListString(fv.value);
    return uids
      .map((uid) => this.findInstanceByUid(uid, mappedInstances))
      .filter((instance): instance is LpBaseInstance => instance !== undefined); // Filter out undefined values
  }

  private resolveVariableReference(fv: FieldValueWithTypeInfo, variables: Variable[]): Variable | undefined {
    if (!fv.value) {
      return undefined;
    }
    const variable = variables.find((variable) => variable.variableRef === fv.value);
    if (!variable) {
      console.warn(`Variable reference not found: ${fv.value}. Try clearing the cache.`);
      return undefined;
    }
    return variable;
  }

  private findInstanceByUid(uid: string, mappedInstances: LpBaseInstance[]): LpBaseInstance | undefined {
    const foundInstance = mappedInstances.find((instance) => instance.uid === uid);
    if (!foundInstance) {
      console.warn(`Object reference not found: ${uid}`);
      return undefined;
    }
    return foundInstance;
  }

  private async mapInstance(instance: DataInstance, mappedInstances: LpBaseInstance[], newObject: any, schema: Schema) {
    const typeInfo = this.getTypeInfoForInstance(instance, schema);
    const typedFieldValues = instance.fieldValues.map((fieldValue) => {
      return { ...fieldValue, type: typeInfo[fieldValue.field].type, required: typeInfo[fieldValue.field].required };
    });

    for (const fv of typedFieldValues) {
      newObject[fv.field] = await this.mapFieldValue(fv, mappedInstances, schema.variables);
    }
    return newObject;
  }

  private getTypeInfoForInstance(
    instance: DataInstance,
    schema: Schema,
  ): Record<string, { type: keyof FieldTypeMapping; required: boolean }> {
    // Use cached dictionary if available
    if (this.fieldTypeDictionaryCache[instance.dataType]) {
      return this.fieldTypeDictionaryCache[instance.dataType];
    }
    const structType = schema.structTypes.find((st) => st.typeId === instance.dataType);
    if (!structType) {
      return {};
    }
    const fieldTypeDictionary = structType.fields.reduce(
      (acc, field) => {
        let type = field.type as keyof FieldTypeMapping; // default from schema
        if (
          field.type.startsWith('Struct<') ||
          field.type.startsWith('StructRef<') ||
          field.type.startsWith('Enum<') ||
          field.type.startsWith('EnumRef<')
        ) {
          type = 'ObjectReference';
        }
        if (field.type.startsWith('List<')) {
          type = 'ObjectReferenceList';
        }
        if (field.type.startsWith('Select<')) {
          type = 'Select';
        }
        acc[field.fieldId] = { type, required: field.required };
        return acc;
      },
      {} as Record<string, { type: keyof FieldTypeMapping; required: boolean }>,
    );
    this.fieldTypeDictionaryCache[instance.dataType] = fieldTypeDictionary;
    return fieldTypeDictionary;
  }

  private handleMissingRequiredFields(mappedObjects: LpBaseInstance[], schema: Schema): LpBaseInstance[] {
    const fieldsWithMissingValues: {
      uid: string;
      mappedInstance: LpBaseInstance & Record<string, string>;
      instanceType: string;
      fieldId: string;
    }[] = [];

    mappedObjects = mappedObjects.map((obj) => {
      const extendedObj: LpBaseInstance & Record<string, string> = { ...obj };
      const structType = schema.structTypes.find((st) => st.typeId === extendedObj.dataType);
      if (!structType) return extendedObj;

      for (const field of structType.fields) {
        if (field.required && (!(field.fieldId in extendedObj) || extendedObj[field.fieldId] === undefined)) {
          if (this.#showWarnings) {
            console.warn(`Field ${field.fieldId} is required but missing in object ${extendedObj.uid}`);
          }
          fieldsWithMissingValues.push({
            uid: extendedObj.uid,
            mappedInstance: extendedObj,
            instanceType: extendedObj.dataType,
            fieldId: field.fieldId,
          });
        }
      }
      return extendedObj;
    });

    if (fieldsWithMissingValues.length > 0 && this.#showWarnings) {
      console.warn('Missing required fields:', fieldsWithMissingValues);
      const uniqueFieldsWithMissingValues = Array.from(new Set(fieldsWithMissingValues.map((mf) => `${mf.instanceType}/${mf.fieldId}`)));
      console.warn('Unique missing required fields:', uniqueFieldsWithMissingValues);
    }
    return mappedObjects;
  }

  private unwrapListString(listString: string): string[] {
    //it starts with '[' and ends with ']', and the ids are separated by ','
    //furthemore, the ids are wrapped in double quotes
    //example: '["id1","id2","id3"]'. Empty list is '[]'
    if (listString === '[]') {
      return [];
    }
    return listString
      .substring(1, listString.length - 1)
      .split(',')
      .map((id) => id.substring(1, id.length - 1));
  }
}

// Define a type mapping for fieldTypes to their corresponding TypeScript types
// ObjectReference and ObjectReferenceList are Struct(Ref)s and Enum(Ref)s and their list variants
// so they should be replaced with the actual object later
type FieldTypeMapping = {
  string: string;
  int: number;
  float: number;
  bool: boolean;
  Color: { r: number; g: number; b: number; a: number };
  Vector2: { x: number; y: number };
  Vector3: { x: number; y: number; z: number };
  Icon: FileMeta;
  ImageRef: FileMeta;
  VideoRef: FileMeta;
  AudioRef: FileMeta;
  FileRef: FileMeta;
  VariableRef: Variable;
  Select: string; // this is represented as a string union type, so we can use a string here
  ObjectReference: LpBaseInstance;
  ObjectReferenceList: LpBaseInstance[];
};

interface FieldValueWithTypeInfo extends FieldValue {
  type: keyof FieldTypeMapping;
  required: boolean;
}

interface FieldValueWithGenericType<T extends keyof FieldTypeMapping> extends FieldValueWithTypeInfo {
  type: T;
}
